import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Button, Checkbox, Divider, Flex, Group, Select, Stack, TagsInput, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { AppModal } from "../../shared/AppModal";
import { InfoOutlined } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CharacterLimit } from "../../enums/CharacterLimit";
import { FILE_TYPE_OPTIONS } from "../../enums/FileType";
import { FileTypeEnum, useGetCaregiverAndPatientInfoForFileUploadQuery, useUpdateFileRecordMutation } from "../../graphql/graphql";
import { useFileUpload } from "../../hooks/useFileUpload";
import { doesArrayHaveData } from "../../utils/Array.utils";
import { generateVideoThumbnail, getFileIconColor, getFileTypeFromMimeType } from "../../utils/File.utils";
import { FileUpload } from "../../shared/FileUpload/FileUpload";
import { showErrorNotification, showSuccessNotification } from "../../shared/Notifications";
export const UploadFileModal = ({ fileInfo, opened, close, initialFiles = [] }) => {
    // Destructure File Info
    const { fileId } = fileInfo ?? {};
    // Hooks
    const { priorityEntryId } = useParams();
    const { uploadFiles, isUploadingFile } = useFileUpload();
    // State
    const [formFiles, setFormFiles] = useState([]);
    const [acknowledged, setAcknowledged] = useState(false);
    // NOTE: this modal is global and does not unmount between openings.
    // Keep initialValues static and explicitly set/reset values when opening/closing.
    const UploadFileInitialValues = {
        isShared: false,
        tags: [],
        name: "",
        fileType: FileTypeEnum.PdfUpload
    };
    // Form
    const form = useForm({
        initialValues: UploadFileInitialValues
    });
    // Queries
    const [{ data: caregiverAndPatientInfo }] = useGetCaregiverAndPatientInfoForFileUploadQuery({
        variables: {
            priorityEntryId: priorityEntryId ?? ""
        },
        pause: !priorityEntryId || !opened
    });
    // Mutations
    const [, updateFile] = useUpdateFileRecordMutation();
    // Data
    const hasFiles = doesArrayHaveData(formFiles);
    const caregiverId = caregiverAndPatientInfo?.priorityEntries?.priorityEntry?.caregiverId;
    const patientId = caregiverAndPatientInfo?.priorityEntries?.priorityEntry?.patientId;
    // Effects
    useEffect(() => {
        // Only apply dropped files when the modal is open and we're uploading (not editing)
        if (opened && !fileId && doesArrayHaveData(initialFiles)) {
            handleSetFiles(initialFiles);
        }
    }, [initialFiles, opened, fileId]);
    // Update form values when fileInfo changes
    useEffect(() => {
        if (opened) {
            // Always set explicit values on open to avoid stale state between modal openings
            if (fileId) {
                form.setValues({
                    isShared: fileInfo?.isShared ?? false,
                    tags: fileInfo?.tags?.filter((t) => t !== null) ?? [],
                    name: fileInfo?.name ?? "",
                    fileType: fileInfo?.fileType ??
                        FileTypeEnum.PdfUpload
                });
            }
            else {
                form.setValues(UploadFileInitialValues);
            }
            form.resetDirty();
        }
    }, [fileInfo, opened, fileId]);
    // Reset local state when the modal closes (component persists between openings)
    useEffect(() => {
        if (!opened) {
            formFiles.forEach((f) => URL.revokeObjectURL(f.url));
            setFormFiles([]);
            setAcknowledged(false);
            form.setValues(UploadFileInitialValues);
            form.resetDirty();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened]);
    // Cleanup URLs
    useEffect(() => {
        return () => {
            formFiles.forEach((f) => URL.revokeObjectURL(f.url));
        };
    }, []);
    // Handlers
    const handleSetFiles = async (newFiles) => {
        const entries = await Promise.all(newFiles.map(async (file) => {
            let thumbnailUrl;
            if (file.type.startsWith("video/")) {
                try {
                    thumbnailUrl = await generateVideoThumbnail(file);
                }
                catch (e) {
                    console.warn("Could not generate video thumbnail", e);
                }
            }
            return {
                file,
                url: URL.createObjectURL(file),
                thumbnailUrl,
                name: file.name
                    .replace(/\.[^/.]+$/, "")
                    .replace(/[^a-zA-Z0-9]/g, " "),
                fileType: getFileTypeFromMimeType(file.type)
            };
        }));
        setFormFiles((prev) => [...prev, ...entries]);
    };
    const removeFile = (index) => {
        setFormFiles((prev) => {
            const newFiles = [...prev];
            URL.revokeObjectURL(newFiles[index].url);
            newFiles.splice(index, 1);
            return newFiles;
        });
    };
    const updateFileField = (index, field, value) => {
        setFormFiles((prev) => {
            const newFiles = [...prev];
            newFiles[index] = { ...newFiles[index], [field]: value };
            return newFiles;
        });
    };
    const submitFileUpload = async (event) => {
        event.preventDefault();
        if (!hasFiles) {
            showErrorNotification("Please select at least one file to upload");
            return;
        }
        const uploads = formFiles.map((f) => ({
            file: f.file,
            name: f.name || f.file.name,
            fileType: f.fileType,
            tags: form.values.tags,
            isShared: form.values.isShared,
            caregiverId,
            patientId
        }));
        await uploadFiles(uploads).then(() => {
            handleClose();
        });
    };
    const handleUpdateFile = async () => {
        if (!fileId)
            return;
        await updateFile({
            input: {
                fileId,
                name: form.values.name,
                fileType: form.values.fileType,
                tags: form.values.tags,
                isShared: form.values.isShared,
                caregiverId: caregiverId,
                patientId: patientId
            }
        })
            .then(() => {
            showSuccessNotification("File updated successfully");
            handleClose();
        })
            .catch(() => {
            showErrorNotification("Failed to update file");
        });
    };
    const handleClose = () => {
        formFiles.forEach((f) => URL.revokeObjectURL(f.url));
        setFormFiles([]);
        setAcknowledged(false);
        form.setValues(UploadFileInitialValues);
        form.resetDirty();
        close();
    };
    return (_jsx(AppModal, { size: !!fileInfo ? "md" : "lg", title: _jsx(Title, { order: 4, lineClamp: 1, children: fileId
                ? "Update File"
                : hasFiles
                    ? `Upload ${formFiles.length} Files`
                    : "Upload File" }), opened: opened, onClose: handleClose, children: _jsxs(Stack, { gap: "sm", children: [!fileId && (_jsx(FileUpload, { isEditing: !!fileInfo, files: formFiles, setFiles: handleSetFiles, onRemove: removeFile, onUpdate: updateFileField, fileTypeOptions: FILE_TYPE_OPTIONS })), hasFiles && !fileId && (_jsx(Group, { justify: "flex-end", children: _jsxs(Button, { variant: "subtle", size: "compact-xs", color: "gray", onClick: () => {
                            formFiles.forEach((f) => URL.revokeObjectURL(f.url));
                            setFormFiles([]);
                        }, children: ["Clear all ", formFiles.length, " files"] }) })), fileId && (_jsxs(Flex, { gap: "xs", children: [_jsx(TextInput, { "data-autofocus": true, required: true, label: "File Name", placeholder: "File Name", style: { flex: 2 }, maxLength: CharacterLimit.FILE_NAME, ...form.getInputProps("name") }), _jsx(Select, { label: "File Type", placeholder: "File Type", style: { flex: 1 }, clearable: true, searchable: true, checkIconPosition: "right", data: FILE_TYPE_OPTIONS, ...form.getInputProps("fileType") })] })), _jsx(Divider, { label: "Common Settings", labelPosition: "center" }), _jsx(TagsInput, { styles: {
                        pill: {
                            fontWeight: 600,
                            color: "var(--mantine-color-white)",
                            backgroundColor: getFileIconColor(form.values.fileType)
                        }
                    }, label: "Tags", placeholder: "Add tags to these files...", clearable: true, acceptValueOnBlur: true, splitChars: [","], maxLength: CharacterLimit.TAGS_LENGTH, maxTags: CharacterLimit.TAGS_COUNT, ...form.getInputProps("tags") }), priorityEntryId && !fileId && (_jsx(Alert, { variant: "light", title: "Sharing Notice", icon: _jsx(InfoOutlined, {}), color: "var(--mantine-color-secondary-5)", p: "xs", children: _jsx(Checkbox, { label: "By uploading files attached to this client, I acknowledge they may be shared with other organizations actively supporting this client.", checked: acknowledged, onChange: (event) => setAcknowledged(event.currentTarget.checked), styles: {
                            label: {
                                color: "var(--mantine-color-blue-9)",
                                fontSize: "11px"
                            }
                        } }) })), _jsxs(Flex, { justify: "space-between", align: "center", style: { marginTop: "0.5rem" }, children: [_jsx(Checkbox, { label: "Share with Therapprove?", checked: form.values.isShared, onChange: (event) => {
                                form.setFieldValue("isShared", event.currentTarget.checked);
                            } }), _jsx(Button, { variant: "filled", loading: isUploadingFile, onClick: fileId ? handleUpdateFile : submitFileUpload, disabled: !!priorityEntryId && !acknowledged && !fileId, children: fileId
                                ? "Update"
                                : hasFiles
                                    ? `Upload ${formFiles.length} Files`
                                    : "Upload" })] })] }) }));
};
